import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { History as FeedIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { relativeDate } from '../../../../utils/dateUtils';
import { formatSourceRef } from '../../../../utils/sourceRef';
import { useVenue } from '../../../../context/VenueContext';
import PageHeader from '../../../../components/venue/PageHeader';
import FilterBar from '../../../../components/venue/FilterBar';
import EmptyState from '../../../../components/venue/EmptyState';
import inventoryFeedService from '../../../../services/inventoryFeedService';
import venueLocationService from '../../../../services/venueLocationService';
import MovementTypeBadge from '../../../../components/venue/MovementTypeBadge';
import type { MovementFeedRow, MovementType, VenueLocation } from '../../../../types/venue';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const COL_COUNT = 6; // Wine, Type, Qty, Location, Date, Source

const MOVEMENT_TYPE_OPTIONS: { value: MovementType; label: string }[] = [
  { value: 'RECEIVE',            label: 'Receive' },
  { value: 'DEPLETE',            label: 'Deplete' },
  { value: 'TRANSFER_IN',        label: 'Transfer In' },
  { value: 'TRANSFER_OUT',       label: 'Transfer Out' },
  { value: 'COUNT_VARIANCE',     label: 'Count Variance' },
  { value: 'MANUAL_ADJUSTMENT',  label: 'Adjustment' },
  { value: 'WASTE',              label: 'Waste' },
  { value: 'RETURN_TO_SUPPLIER',    label: 'Return to Supplier' },
  { value: 'PRODUCTION_CONSUMPTION', label: 'Production Consumption' },
  { value: 'PRODUCTION_OUTPUT',      label: 'Production Output' },
  { value: 'OPENING_BALANCE',        label: 'Opening Stock' },
];

const SOURCE_DOC_LABELS: Record<string, string> = {
  GOODS_RECEIPT:  'Receipt',
  PURCHASE_ORDER: 'PO',
  STOCK_COUNT:    'Stock Count',
  TRANSFER:          'Transfer',
  MANUAL:            'Manual',
  POS_ORDER:         'POS Order',
  PRODUCTION_ORDER:  'Production',
  // BASIC-ITEM.C6 — a POS set-on-hand. Unlike the register and item detail,
  // which humanize any source generically via replace(/_/g,' '), this screen
  // reads a label map and falls back to the RAW value — so without an entry a
  // count variance would show "POS_COUNT" next to "POS Order". Added for
  // consistency with the map's own convention, not as new behavior.
  POS_COUNT:         'POS Count',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function signedQty(qty: number): string {
  return qty > 0 ? `+${qty}` : String(qty);
}

function qtyColor(qty: number): string {
  if (qty > 0) return '#1a7f37';
  if (qty < 0) return '#cf222e';
  return '#6e7781';
}

/**
 * POS-ORDER-NUMBER.1 — the order NUMBER when the movement carries one.
 *
 * This showed the last 6 characters of an ObjectId, which matches nothing a
 * person reconciling against the POS can see. `full` is the identity, for the
 * tooltip; the number is unique only per company, so matching is still on the id.
 */
function fmtSource(doc: MovementFeedRow['sourceDocument']): { text: string; full: string } {
  if (!doc) return { text: '—', full: '' };
  const label = SOURCE_DOC_LABELS[doc.type] ?? doc.type;
  const ref = formatSourceRef(doc.id, doc.number);
  return {
    text: ref ? `${label}·\u202f${ref.text}` : label,
    full: ref?.full ?? '',
  };
}

// ── Component ─────────────────────────────────────────────────────────────────

const InventoryMovementFeed: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { venueId } = useVenue();

  // ── Filter state — initialise type from URL so dashboard can link with preset ─
  const [typeFilter, setTypeFilter] = useState<MovementType | ''>(() => {
    const t = searchParams.get('type') as MovementType | null;
    return MOVEMENT_TYPE_OPTIONS.some(o => o.value === t) ? (t as MovementType) : '';
  });
  const [locationFilter, setLocationFilter] = useState('');

  // ── Data ──────────────────────────────────────────────────────────────────────
  const [rows, setRows] = useState<MovementFeedRow[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0); // MUI 0-based; API 1-based
  const [pageSize, setPageSize] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [locations, setLocations] = useState<VenueLocation[]>([]);

  // ── Load locations once ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(setLocations)
      .catch(() => { /* non-blocking */ });
  }, [venueId]);

  // ── Load feed ─────────────────────────────────────────────────────────────────
  const loadFeed = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await inventoryFeedService.getFeed(venueId, {
        page: page + 1,
        pageSize,
        type: typeFilter || undefined,
        locationId: locationFilter || undefined,
      });
      setRows(result.data ?? []);
      setTotalItems(result.pagination?.totalItems ?? 0);
    } catch {
      setError('Failed to load movement feed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [venueId, page, pageSize, typeFilter, locationFilter]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <PageHeader
        icon={<FeedIcon />}
        title="Movement History"
        count={!loading && totalItems > 0 ? totalItems : undefined}
        subtitle="All stock changes across all items · transfers appear as separate stock-out and stock-in entries"
      />

      {/* Filters */}
      <FilterBar>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Movement type</InputLabel>
          <Select
            value={typeFilter}
            label="Movement type"
            onChange={e => { setTypeFilter(e.target.value as MovementType | ''); setPage(0); }}
          >
            <MenuItem value="">All types</MenuItem>
            {MOVEMENT_TYPE_OPTIONS.map(o => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Location</InputLabel>
          <Select
            value={locationFilter}
            label="Location"
            onChange={e => { setLocationFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All locations</MenuItem>
            {locations.map(loc => (
              <MenuItem key={loc._id} value={loc._id}>{loc.displayName}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      {/* Error */}
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Wine', 'Type', 'Qty', 'Location', 'Date', 'Source'].map(h => (
                  <TableCell key={h}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COL_COUNT}>
                    <Box sx={{ py: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <CircularProgress size={28} />
                      <Typography variant="body2" color="text.secondary">Loading movement history…</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COL_COUNT}>
                    <EmptyState
                      icon={FeedIcon}
                      message={typeFilter || locationFilter
                        ? 'No movements match the current filters.'
                        : 'No movements yet.'}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow key={`${row._id}-${index}`} hover>
                    {/* Wine */}
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {row.displayName ?? '—'}
                      </Typography>
                      {row.producerName && (
                        <Typography variant="caption" color="text.secondary">
                          {row.producerName}
                        </Typography>
                      )}
                    </TableCell>
                    {/* Type */}
                    <TableCell>
                      <MovementTypeBadge type={row.type} />
                    </TableCell>
                    {/* Qty */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        sx={{ color: qtyColor(row.quantity), fontFamily: 'monospace' }}
                      >
                        {signedQty(row.quantity)}
                      </Typography>
                    </TableCell>
                    {/* Location */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {row.locationName ?? '—'}
                      </Typography>
                    </TableCell>
                    {/* Date */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                        {relativeDate(row.postedAt)}
                      </Typography>
                    </TableCell>
                    {/* Source */}
                    <TableCell>
                      <Tooltip title={fmtSource(row.sourceDocument).full} placement="top">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                        >
                          {fmtSource(row.sourceDocument).text}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {totalItems > pageSize && (
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={e => { setPageSize(Number(e.target.value)); setPage(0); }}
          />
        )}
      </Paper>

    </Box>
  );
};

export default InventoryMovementFeed;
